import { CheckCircleIcon } from "@heroicons/react/20/solid";
import {
    ChatBubbleBottomCenterTextIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";
import { CommunityRelationshipType } from "@sp-crm/core";
import { ToursSection } from "components/tours/tours-section";
import { AutosavingInput } from "components/ui/autosaving-input";
import { defaultLinkStyle } from "components/ui/link";
import React from "react";
import { ClientCommunityRelationship } from "../types";

interface CommunityCardShortlistExperienceProps {
    clientCommunityRelationship: ClientCommunityRelationship;
}

export const CommunityCardShortlistExperience: React.FC<
    CommunityCardShortlistExperienceProps
> = props => {
    const {
        privateNote,
        publicNote,
        updateRelationship,
        relationship,
        referred,
        tours,
        clientId,
        clientName,
        communityId,
        communityName,
        onToursChanged,
    } = props.clientCommunityRelationship;
    const [isPrivateNoteVisible, setIsPrivateNoteVisible] = React.useState(!!privateNote);
    const showPrivateNotes = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setIsPrivateNoteVisible(true);
        },
        [],
    );
    const savePublicNote = React.useCallback(
        (newNote: string) => {
            updateRelationship({
                action: "updateNote",
                note: newNote,
                noteType: "public",
            });
        },
        [updateRelationship],
    );
    const savePrivateNote = React.useCallback(
        (newNote: string) => {
            updateRelationship({
                action: "updateNote",
                note: newNote,
                noteType: "private",
            });
        },
        [updateRelationship],
    );
    return (
        <>
            <div className="p-2 lg:p-6">
                <AutosavingInput
                    multiLine={true}
                    onCommit={savePublicNote}
                    initial={publicNote ?? ""}
                    disabled={relationship !== CommunityRelationshipType.featured}
                    label={
                        <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                            <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                            <span>
                                {relationship === CommunityRelationshipType.featured
                                    ? "Notes (shared with family)"
                                    : "Notes (add to family view to edit notes)"}
                            </span>
                        </h4>
                    }
                />
            </div>
            <div className="p-2 lg:p-6">
                {isPrivateNoteVisible ? (
                    <AutosavingInput
                        multiLine={true}
                        onCommit={savePrivateNote}
                        initial={privateNote ?? ""}
                        label={
                            <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                                <LockClosedIcon className="h-4 w-4" />
                                <span>Internal notes (not shared)</span>
                            </h4>
                        }
                    />
                ) : (
                    <a href="#" className={defaultLinkStyle} onClick={showPrivateNotes}>
                        Add internal notes
                    </a>
                )}
            </div>
            {clientId && communityId && onToursChanged ? (
                <div className="p-2 lg:p-6 border-t border-gray-200">
                    <ToursSection
                        tours={tours || []}
                        clientId={clientId}
                        clientName={clientName}
                        communityId={communityId}
                        communityName={communityName}
                        onToursChanged={onToursChanged}
                    />
                </div>
            ) : null}
            <div className="-mt-px flex divide-x divide-gray-200 cursor-pointer">
                <div className="flex w-0 flex-1">
                    <a
                        onClick={e => {
                            e.preventDefault();
                            updateRelationship({
                                action: "removeRelationship",
                            });
                        }}
                        className={`relative inline-flex w-0 flex-1 items-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 bg-brand-100 hover:bg-gray-100 hover:text-gray-800 twoverride`}>
                        {referred ? (
                            <div className="flex flex-col items-center justify-center w-full space-y-2">
                                <div>Potential match</div>
                                <div className="flex items-center space-x-2 text-sm">
                                    <CheckCircleIcon className="w-3 h-3" />
                                    <span>Referred</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center w-full space-y-2">
                                <div>Potential match</div>
                                <div className="font-bold">Not yet referred</div>
                            </div>
                        )}
                    </a>
                </div>
                <div className="-ml-px flex w-0 flex-1">
                    {relationship === CommunityRelationshipType.featured ? (
                        <a
                            onClick={e => {
                                e.preventDefault();
                                updateRelationship({
                                    action: "updateRelationship",
                                    relationship: CommunityRelationshipType.matched,
                                });
                            }}
                            className="relative inline-flex w-0 flex-1 items-center justify-center rounded-br-lg border border-transparent py-4 text-sm font-medium bg-green-100 text-green-600 hover:bg-gray-100 hover:text-gray-800 twoverride group">
                            <CheckCircleIcon className="w-4 h-4 text-green-800 group-hover:text-grey-800" />
                            <span className="ml-2">In family view</span>
                        </a>
                    ) : (
                        <a
                            onClick={e => {
                                e.preventDefault();
                                updateRelationship({
                                    action: "updateRelationship",
                                    relationship: CommunityRelationshipType.featured,
                                });
                            }}
                            className="relative inline-flex w-0 flex-1 items-center justify-center rounded-br-lg border border-transparent py-4 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 twoverride">
                            <span className="ml-3">Show in family view</span>
                        </a>
                    )}
                </div>
            </div>
        </>
    );
};
